/**
 * A show style + a studio → the production config (D32): what the engine and
 * the device host consume (`productionConfig.ts`, the frozen pre-D32 shape),
 * with the PAIR validated.
 *
 * The pair is valid when:
 *   - every show-style layer a piece type's template uses is mapped in the
 *     studio (`studio.mappings.<layer>`);
 *   - each such mapping's device is a driver the layer allows
 *     (`showStyle.layers.<layer>.deviceTypes`, empty = any);
 *   - a template that names its `content.deviceType` names the mapped
 *     device's (TSR routes an object to a device only when they match: a
 *     mismatch is a layer that silently does nothing on air).
 * A declared layer no template uses may stay unmapped. Every problem carries
 * a path prefixed `showStyle.` or `studio.` — the document to fix.
 *
 * The resolved config holds the studio's devices and settings, the studio's
 * mappings of the show style's layers (not the mappings it keeps for other
 * show styles), and the show style's layers, piece types, actions and panels.
 */
import {
  type ConfigProblem,
  type DeviceTypeName,
  type ProductionConfig,
  productionConfigSchema,
  TIMELINE_DEVICE_TYPE,
} from './productionConfig.js';
import { layerUsers, type ShowStyleConfig } from './showStyle.js';
import type { StudioConfig } from './studio.js';

/** One show-style layer against a studio: who uses it, where it is mapped, and what is wrong with that (the SPA's mapping table). */
export type LayerMappingStatus = {
  readonly layer: string;
  readonly label: string;
  /** The drivers the layer allows; empty = any. */
  readonly deviceTypes: readonly DeviceTypeName[];
  /** The piece types whose templates target it; empty = declared but unused. */
  readonly usedBy: readonly string[];
  /** The studio's mapping of it, or null. */
  readonly mapping: { readonly device: string; readonly deviceType: DeviceTypeName | null } | null;
  /** Why the pair is invalid on this layer (unmapped while used, an incompatible device), or null. */
  readonly problem: string | null;
};

function layerProblem(layer: string, usedBy: readonly string[], allowed: readonly DeviceTypeName[], mapping: LayerMappingStatus['mapping']): string | null {
  if (mapping === null) return usedBy.length === 0 ? null : `show-style layer "${layer}" (used by ${usedBy.map((id) => `"${id}"`).join(', ')}) is not mapped to a device of the studio`;
  if (mapping.deviceType === null) return `mapped to unknown device "${mapping.device}"`;
  if (allowed.length > 0 && !allowed.includes(mapping.deviceType)) {
    return `device "${mapping.device}" is a ${mapping.deviceType}; show-style layer "${layer}" needs ${allowed.length === 1 ? 'a' : 'one of'} ${allowed.join(', ')}`;
  }
  return null;
}

/** Every layer the show style declares, against the studio's mappings, in layer-id order. */
export function layerMappingStatus(showStyle: ShowStyleConfig, studio: StudioConfig): LayerMappingStatus[] {
  const users = layerUsers(showStyle);
  return Object.keys(showStyle.layers)
    .sort()
    .map((layer) => {
      const declared = showStyle.layers[layer];
      const usedBy = users.get(layer) ?? [];
      const mapped = studio.mappings[layer];
      const mapping = mapped === undefined ? null : { device: mapped.device, deviceType: studio.devices[mapped.device]?.type ?? null };
      const deviceTypes = declared?.deviceTypes ?? [];
      return { layer, label: declared?.label ?? '', deviceTypes, usedBy, mapping, problem: layerProblem(layer, usedBy, deviceTypes, mapping) };
    });
}

/** Templates naming a `content.deviceType` other than their layer's mapped device's. */
function deviceTypeProblems(showStyle: ShowStyleConfig, studio: StudioConfig): ConfigProblem[] {
  const problems: ConfigProblem[] = [];
  for (const [id, type] of Object.entries(showStyle.pieceTypes)) {
    type.timeline.forEach((entry, index) => {
      const named = entry.content['deviceType'];
      const mapped = studio.mappings[entry.layer];
      const device = mapped === undefined ? undefined : studio.devices[mapped.device];
      if (typeof named !== 'string' || mapped === undefined || device === undefined || named === TIMELINE_DEVICE_TYPE[device.type]) return;
      problems.push({
        path: ['showStyle', 'pieceTypes', id, 'timeline', index, 'content', 'deviceType'],
        message: `"${named}" is not the ${device.type} device "${mapped.device}" the studio maps layer "${entry.layer}" to (it takes "${TIMELINE_DEVICE_TYPE[device.type]}")`,
      });
    });
  }
  return problems;
}

export type ResolveResult = { readonly ok: true; readonly config: ProductionConfig } | { readonly ok: false; readonly problems: readonly ConfigProblem[] };

/** The production config of a parsed show style and studio, or every problem of the pair (paths prefixed by the document). */
export function resolveProductionConfig(showStyle: ShowStyleConfig, studio: StudioConfig): ResolveResult {
  const problems: ConfigProblem[] = [];
  for (const status of layerMappingStatus(showStyle, studio)) {
    if (status.problem === null) continue;
    problems.push({ path: status.mapping === null ? ['studio', 'mappings', status.layer] : ['studio', 'mappings', status.layer, 'device'], message: status.problem });
  }
  problems.push(...deviceTypeProblems(showStyle, studio));
  if (problems.length > 0) return { ok: false, problems };

  const mappings = Object.fromEntries(Object.entries(studio.mappings).filter(([layer]) => Object.hasOwn(showStyle.layers, layer)));
  const parsed = productionConfigSchema.safeParse({
    devices: studio.devices,
    mappings,
    sourceLayers: showStyle.sourceLayers,
    outputLayers: showStyle.outputLayers,
    pieceTypes: showStyle.pieceTypes,
    actions: showStyle.actions,
    adlibPanels: showStyle.adlibPanels,
    settings: studio.settings,
  });
  if (parsed.success) return { ok: true, config: parsed.data };
  // Both halves parsed on their own, so this is a check only the whole can make; name the resolved path.
  return { ok: false, problems: parsed.error.issues.map((issue) => ({ path: ['resolved', ...issue.path.map((p) => (typeof p === 'symbol' ? String(p) : p))], message: issue.message })) };
}
